import has from "lodash-es/has";
import includes from "lodash-es/includes";
import isEqual from "lodash-es/isEqual";
import isNil from "lodash-es/isNil";
import isUndefined from "lodash-es/isUndefined";
import keyBy from "lodash-es/keyBy";
import keys from "lodash-es/keys";
import pull from "lodash-es/pull";
import uniq from "lodash-es/uniq";
import unset from "lodash-es/unset";
import values from "lodash-es/values";
import { action, makeAutoObservable, runInAction } from "mobx";
import type { z } from "zod";
import { apiClient } from "../common/apiClient";
import { logger } from "../common/logger";
import { platformRoutes, resolvePlatformRequestUrl } from "../common/platformRoutes";
import type { BaseStoreType } from "../common/types/BaseStoreType";
import {
   type AsyncData,
   initializeWithDefaultData,
   setAsDataAvailable,
   setAsErrorOccured,
   setAsLoading,
   setAsWaitingForData
} from "../common/utils/asyncDataUtils";
import type {
   CreateListQueryParams,
   CreateListResponse,
   GetListsResponse,
   UpdateListBody,
   UpdateListNameResponse
} from "../common/zodSchemas/backend";
import theme from "../themes/theme";
import authStore from "./auth/authStore";
export type FavoriteList = {
   id: string;
   listName: string;
   items: Record<string, number>;
   sequence: string[];
};

type CreateListQueryParamsType = z.infer<typeof CreateListQueryParams>;
type CreateListResponseType = z.infer<typeof CreateListResponse>;

type UpdateListBodyType = z.infer<typeof UpdateListBody>;

type UpdateListNameResponseType = z.infer<typeof UpdateListNameResponse>;

type GetListsResponseType = z.infer<typeof GetListsResponse>;

type FavoriteStoreType = BaseStoreType & {
   allFavoriteLists: AsyncData<Record<string, FavoriteList>>;
   allSkus: string[];
   checkedListItems: FavoriteList["items"];
   currentFavoriteList: FavoriteList["items"];
   saveFavoritesProgress: AsyncData<null>;
   isFavorite(sku: string): boolean;
   addFavorite(sku: string, listId?: string, qty?: number): void;
   addMultipleFavorites(skus: string[], listId: string, items: FavoriteList["items"]): void;
   removeFavorite(sku: string, listId?: string): void;
   syncAllSkus(): void;
   getFavoriteListById(id: string): FavoriteList | undefined;
   getFavoriteListByName(name: string): FavoriteList | undefined;
   createNewListInStorage(listName: string): Promise<FavoriteList>;
   changeListNameInStorage(listId: string, newListName: string): Promise<void>;
   deleteList(listId: string): Promise<void>;
   loadFavorites(): Promise<void>;
   clearSaveFavoritesProgress(): void;
   saveFavorites(listId: string, performSync?: boolean): Promise<void>;
};

const isAbleToAddFavorite = (list: FavoriteList | undefined) => {
   if (!authStore.isLoggedIn()) {
      logger.warn("favorites", "Attempting to add favorite without being logged in");
      return false;
   }
   if (!list) {
      logger.warn("favorites", "Unable to find list to add favorite to");
      return false;
   }
   return true;
};

const isFavoriteInList = (sku: string, listId: string) => {
   const list = favoriteStore.getFavoriteListById(listId);
   return !!list && includes(list.sequence, sku);
};

const getUrl = (customerNumber: string) =>
   resolvePlatformRequestUrl(platformRoutes.favorites, {
      storeId: theme.storeId,
      customerNumber
   });

const getListUrl = (customerNumber: string, listId: string) =>
   resolvePlatformRequestUrl(platformRoutes.favorite, {
      storeId: theme.storeId,
      customerNumber,
      id: listId
   });

const getListNameUrl = (customerNumber: string, listId: string) =>
   resolvePlatformRequestUrl(platformRoutes.favoriteName, {
      storeId: theme.storeId,
      customerNumber,
      id: listId
   });

const favoriteStore: FavoriteStoreType = makeAutoObservable({
   allFavoriteLists: initializeWithDefaultData({}),
   allSkus: [],
   checkedListItems: {},
   currentFavoriteList: {},
   saveFavoritesProgress: initializeWithDefaultData(null),

   isFavorite: (sku) => {
      return includes(favoriteStore.allSkus, sku);
   },

   addFavorite: action((sku, listId, qty = 1) => {
      const favoriteLists = values(favoriteStore.allFavoriteLists.data);
      const list = isNil(listId) ? values(favoriteLists)[0] : favoriteStore.getFavoriteListById(listId);
      if (isAbleToAddFavorite(list) && !!list) {
         list.items[sku] = qty;
         list.sequence.push(sku);
         void favoriteStore.saveFavorites(list.id).catch(() => {
            const currentList = favoriteStore.allFavoriteLists.data[list.id];
            if (!currentList) return;
            unset(currentList.items, sku);
            pull(currentList.sequence, sku);
            favoriteStore.syncAllSkus();
         });
      } else if (!listId && !favoriteLists.length) {
         favoriteStore.createNewListInStorage("Mine favoritter").then((list) => {
            list.items[sku] = qty;
            list.sequence.push(sku);
            void favoriteStore.saveFavorites(list.id);
         });
      }
   }),

   addMultipleFavorites: action((skus, listId, items) => {
      const list = isNil(listId) ? values(favoriteStore.allFavoriteLists.data)[0] : favoriteStore.getFavoriteListById(listId);
      if (isAbleToAddFavorite(list) && !!list) {
         for (const sku of skus) {
            if (!isFavoriteInList(sku, listId)) {
               const newItem = { [sku]: items[sku] };
               list.items = { ...list.items, ...newItem };
               list.sequence.push(sku);
            } else {
               list.items[sku] = list.items[sku] + items[sku];
            }
         }
         favoriteStore.allFavoriteLists.data[list.id] = { ...list };
         void favoriteStore.saveFavorites(list.id, true);
      }
   }),

   removeFavorite: action((sku, listId) => {
      if (!authStore.isLoggedIn()) {
         logger.warn("favorites", "Attempting to remove favorite without being logged in");
         return;
      }

      if (!isNil(listId)) {
         // If listId is specified, remove SKU from specific list only
         const list = favoriteStore.getFavoriteListById(listId);
         if (!list) {
            logger.warn("favorites", "Unable to find list to remove favorite from");
            return;
         }
         unset(list.items, sku);
         pull(list.sequence, sku);
         void favoriteStore.saveFavorites(list.id, true);
      } else {
         // If no listId is specified, remove it from all lists
         for (const list of values(favoriteStore.allFavoriteLists.data)) {
            if (has(list.items, sku)) {
               unset(list.items, sku);
               pull(list.sequence, sku);
               void favoriteStore.saveFavorites(list.id, false);
            }
         }

         // Manual sync since we might have changed several lists
         favoriteStore.syncAllSkus();
      }
   }),

   syncAllSkus: action(() => {
      const allSkus = values(favoriteStore.allFavoriteLists.data).flatMap((fl) => keys(fl.items));
      favoriteStore.allFavoriteLists.data = { ...favoriteStore.allFavoriteLists.data };
      favoriteStore.allSkus = uniq(allSkus);
   }),

   getFavoriteListById: (id) => favoriteStore.allFavoriteLists.data[id],

   getFavoriteListByName: (name) => {
      const favoriteList = values(favoriteStore.allFavoriteLists.data);
      return favoriteList.find((favorite) => favorite.listName === name);
   },

   createNewListInStorage: async (listName) => {
      const customerNumber = authStore.currentCompany;
      if (isNil(customerNumber)) {
         logger.warn("favorites", "Attempting to create list before customer number is available", authStore.user);
         return Promise.reject("Not logged in, unable to create new list");
      }

      const params: CreateListQueryParamsType = { listName };
      const favoriteList: CreateListResponseType = await apiClient(getUrl(customerNumber), authStore.getSessionToken())
         .query(params)
         .post()
         .json(
            action((list) => {
               favoriteStore.allFavoriteLists.data[list.id] = list;
               logger.log("favorites", "Created new list: ", listName);
               return list;
            })
         );

      return favoriteList;
   },

   changeListNameInStorage: async (listId: string, newListName: string) => {
      const customerNumber = authStore.currentCompany;
      if (isNil(customerNumber)) {
         logger.warn("favorites", "Attempting to change list name before customer number is available", authStore.user);
         return Promise.reject("Not logged in, unable to change list name");
      }

      const url = getListNameUrl(customerNumber, listId);
      await apiClient(url, authStore.getSessionToken())
         .put(newListName)
         .json(
            action((list: UpdateListNameResponseType) => {
               favoriteStore.allFavoriteLists.data[list.id].listName = list.listName;
               logger.log("favorites", "Changed list name: ", newListName);
            })
         )
         .catch((res) => {
            logger.error("favorites", "Error during changing of list name", res);
            throw res;
         });
   },

   deleteList: (listId) => {
      const customerNumber = authStore.currentCompany;
      if (isNil(customerNumber)) {
         logger.warn("favorites", "Attempting to delete favorites before customer number is available", authStore.user);
         return Promise.reject("Not logged in, unable to delete list");
      }

      return apiClient(getListUrl(customerNumber, listId), authStore.getSessionToken())
         .delete()
         .text(
            action(() => {
               unset(favoriteStore.allFavoriteLists.data, listId);
               favoriteStore.syncAllSkus();
            })
         );
   },

   loadFavorites: async () => {
      const customerNumber = authStore.currentCompany;
      if (isNil(customerNumber)) {
         logger.warn("favorites", "Attempting to load favorites before customer number is available", authStore.user);
         return Promise.reject();
      }

      const favoritesUrl = resolvePlatformRequestUrl(platformRoutes.favorites, {
         storeId: theme.storeId,
         customerNumber
      });

      logger.log("favorites", `Fetching favorites for customer number ${customerNumber}`);
      setAsLoading(favoriteStore.allFavoriteLists);
      try {
         const favorites: GetListsResponseType = await apiClient(favoritesUrl, authStore.getSessionToken()).get().json();
         runInAction(() => {
            favoriteStore.allFavoriteLists.data = keyBy(favorites, "id");

            for (const key of Object.keys(favoriteStore.allFavoriteLists.data)) {
               favoriteStore.allFavoriteLists.data[key].sequence = uniq(favoriteStore.allFavoriteLists.data[key].sequence);
            }

            favoriteStore.syncAllSkus();
         });

         logger.log("favorites", "Favorites loaded: ", favoriteStore.allFavoriteLists);
         setAsDataAvailable(favoriteStore.allFavoriteLists, favoriteStore.allFavoriteLists.data);
      } catch (err) {
         logger.error("favorites", "Error during loading of favorites", err);
         setAsErrorOccured(favoriteStore.allFavoriteLists, `${err}`);
      }
   },

   clearSaveFavoritesProgress: action(() => {
      favoriteStore.saveFavoritesProgress = initializeWithDefaultData(null);
   }),

   saveFavorites: async (listId, performSync = true) => {
      const customerNumber = authStore.currentCompany;
      if (isNil(customerNumber)) {
         logger.warn("favorites", "Attempting to save favorites before customer number is available", authStore.user);
         return Promise.reject("Not logged in, unable to save favorites");
      }

      const list: UpdateListBodyType | undefined = favoriteStore.getFavoriteListById(listId);

      if (isUndefined(list)) {
         logger.warn("favorites", `Unable to find favorite list with id ${listId}`);
         return Promise.reject();
      }

      const optimisticList = { ...list, items: { ...list.items }, sequence: [...list.sequence] };
      favoriteStore.allFavoriteLists.data = { ...favoriteStore.allFavoriteLists.data, [listId]: optimisticList };

      if (performSync) {
         favoriteStore.syncAllSkus();
      }

      setAsWaitingForData(favoriteStore.saveFavoritesProgress);

      return await apiClient(getListUrl(customerNumber, listId), authStore.getSessionToken())
         .put(optimisticList)
         .json(
            action((res) => {
               const currentList = favoriteStore.allFavoriteLists.data[listId];
               if (!currentList) return;
               currentList.listName = res.listName;
               const itemsChanged = !isEqual(currentList.items, res.items);
               if (itemsChanged) {
                  currentList.items = res.items;
                  if (performSync) {
                     favoriteStore.syncAllSkus();
                  }
               }
               setAsDataAvailable(favoriteStore.saveFavoritesProgress);
            })
         )
         .catch((res) => {
            logger.error("favorites", "Unable to save favorites", res);
            setAsErrorOccured(favoriteStore.saveFavoritesProgress, "Unable to save favorites");
            throw res;
         });
   },
   clearCompanySpecificData() {
      runInAction(() => {
         favoriteStore.allFavoriteLists = initializeWithDefaultData({});
         favoriteStore.saveFavoritesProgress = initializeWithDefaultData(null);
         favoriteStore.syncAllSkus();
      });
   }
} satisfies FavoriteStoreType);

export default favoriteStore;
